import { yupResolver } from '@hookform/resolvers/yup';
import { Input, RadioCard, Select } from '@insify/design-system/components';
import { RichContent, WizardLayoutFormWithCommonSection, useWizard } from '@insify/funnel-components';
import { getLocaledMonthNames } from '@insify/ui-utils';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useCommands } from '../../hooks/useCommands';
import { useParamsQuery } from '../../hooks/useQueries';

import styles from './FoundingDate.module.css';

import type { TFunction } from 'i18next';

type CompanyState = 'registered' | 'not-registered';

const shouldShowMonth = (currentYear: number, foundingYear: number): boolean => {
  return currentYear - foundingYear < 5;
};

const validation = (t: TFunction<['funnel', 'common', 'validation']>) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  return yup.object().shape({
    foundingDate: yup.object().shape({
      foundingYear: yup
        .number()
        .typeError(t('validation:required'))
        .min(1750, t('validation:number_too_low', { min: 1750 }))
        .max(currentYear, t('validation:number_too_high', { max: currentYear }))
        .required(t('validation:required')),
      foundingMonth: yup
        .number()
        .optional()
        .when('foundingYear', {
          is: (foundingYear: number) => shouldShowMonth(currentYear, foundingYear),
          then: (schema) => schema.required(t('validation:required')),
        })
        .when('foundingYear', {
          is: (foundingYear: number) => foundingYear === currentYear,
          then: (schema) => schema.max(currentMonth, t('validation:number_too_high')),
        }),
    }),
  });
};

export const FoundingDatePage: React.FC = () => {
  const { t } = useTranslation(['funnel', 'common', 'validation']);
  const { showPreviousPage, showNextPage } = useWizard();
  const { sendCommand } = useCommands();
  const { data: quoteParams, isStale: paramsStale } = useParamsQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyState, setCompanyState] = useState<CompanyState>('registered');
  const [triggerShowingNextPage, setTriggerShowingNextPage] = useState(false);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const { handleSubmit, register, watch, formState } = useForm({
    resolver: yupResolver(validation(t)),
    shouldUnregister: true,
    defaultValues: {
      foundingDate: {
        foundingYear: quoteParams?.foundingDate
          ? new Date(quoteParams.foundingDate as unknown as string).getFullYear()
          : currentYear,
        foundingMonth: quoteParams?.foundingDate
          ? new Date(quoteParams.foundingDate as unknown as string).getMonth() + 1
          : currentMonth,
      },
    },
    mode: 'onSubmit',
  });

  const hasCompletedFoundingDateStep = Boolean(
    triggerShowingNextPage && !paramsStale && !!quoteParams?.companyRegistrationStatus,
  );

  useEffect(() => {
    if (hasCompletedFoundingDateStep) {
      showNextPage();
      setTriggerShowingNextPage(false);
    }
  }, [hasCompletedFoundingDateStep]);

  useEffect(() => {
    setCompanyState(quoteParams?.companyRegistrationStatus ?? 'registered');
  }, [quoteParams?.companyRegistrationStatus]);

  const submit = async ({ foundingDate }: { foundingDate: { foundingYear: number; foundingMonth?: number } }) => {
    setIsSubmitting(true);

    if (companyState === 'registered') {
      await sendCommand({
        type: 'enterFoundingDate',
        payload: foundingDate,
      });
    } else {
      await sendCommand({
        type: 'confirmCompanyNotYetRegistered',
        payload: { keepActivitiesAndClaims: true },
      });
    }

    setTriggerShowingNextPage(true);
  };

  const selectedYear = Number(watch('foundingDate.foundingYear'));
  const selectedMonth = Number(watch('foundingDate.foundingMonth'));

  const monthOptions = getLocaledMonthNames('fr', 'long').map((label, index) => ({ label, value: index + 1 }));
  const monthOptionsUntilThisMonth = selectedYear === currentYear ? monthOptions.slice(0, currentMonth) : monthOptions;

  const showMonth = shouldShowMonth(currentYear, selectedYear);

  return (
    <WizardLayoutFormWithCommonSection
      backCopy={t('common:back')}
      onBack={showPreviousPage}
      forwardCopy={t('common:continue')}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(submit)}
    >
      <div className={styles.introCopy}>
        <RichContent.H2>{t('funnel:founding_date_intro_title')}</RichContent.H2>
        <RichContent.P className={styles.paragraph}>{t('funnel:founding_date_intro_subtitle')}</RichContent.P>
      </div>
      <RadioCard
        onChange={(event) => setCompanyState(event.target.value as CompanyState)}
        onBlur={() => {}}
        name='companyState'
        id='companyState'
        items={[
          {
            id: 'registered-company',
            label: t('founding_date_registered_company'),
            element: (
              <>
                <Input
                  {...register('foundingDate.foundingYear')}
                  id='year'
                  trackingId='year'
                  inputMode='numeric'
                  label={t('funnel:founding_date_year')}
                  defaultValue={selectedYear || currentYear}
                  placeholder={currentYear.toString()}
                  error={formState.errors.foundingDate?.foundingYear?.message}
                  disabled={isSubmitting}
                />
                {showMonth && (
                  <Select
                    {...register('foundingDate.foundingMonth')}
                    id='month'
                    trackingId='month'
                    label={t('funnel:founding_date_month')}
                    defaultValue={selectedMonth || 1}
                    disabled={isSubmitting}
                  >
                    {monthOptionsUntilThisMonth.map(({ label, value }) => (
                      <option key={label} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                )}
              </>
            ),
            value: 'registered',
            checked: companyState === 'registered',
          },
          {
            id: 'non-registered-company',
            label: t('founding_date_non_registered_company'),
            value: 'not-registered',
            checked: companyState === 'not-registered',
          },
        ]}
      />
    </WizardLayoutFormWithCommonSection>
  );
};
